"use client";

import Link from "next/link";
import DirectImage from "@/components/ui/direct-image";
import { Button } from "@/components/ui/button";
import { useCart } from "@/lib/context/cart-context";
import { useSpecials } from "@/lib/context/specials-context";
// Use formatPrice (tax-inclusive, matches product detail page) instead of
// formatPricePreTax. Customers found it confusing to see one price on the
// category card and a higher "with-tax" price on the detail page — this
// brings them into alignment. The Dutchie API delivers prices that already
// include ~13% NY state cannabis tax; we display them as-is.
import { formatPrice } from "@/lib/format-text";
import { createProductSlug } from "@/lib/slug-utils";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { normalizeEffect } from "@/lib/effect-utils";
import type { ProductDisplay } from "@/lib/api/dutchie";
import LowStockBadge, { totalQuantity } from "@/components/shop/low-stock-badge";

interface ProductCardProps {
  product: ProductDisplay;
  buttonClassName?: string;
  variant?: "card" | "list";
}

/**
 * Small helper utilities
 */
// Export this utility for use in category pages
export const normalizeStrainType = (strain?: string): string => {
  if (!strain) return "Unknown";
  const s = strain.toLowerCase();
  if (s.includes("indica") && s.includes("hybrid")) return "Indica Hybrid";
  if (s.includes("sativa") && s.includes("hybrid")) return "Sativa Hybrid";
  if (s.includes("indica")) return "Indica";
  if (s.includes("sativa")) return "Sativa";
  if (s.includes("hybrid")) return "Hybrid";
  return s
    .replace(/[_-]/g, " ")
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

// Non-strain type values that shouldn't show as strain badges
const NON_STRAIN_TYPES = ["thc", "cbd", "one to one", "1:1", "not_applicable", "unknown"];

const isValidStrainType = (strain?: string): boolean => {
  if (!strain) return false;
  const lower = strain.toLowerCase().trim();
  return !NON_STRAIN_TYPES.some(t => lower === t || lower.replace(/[\s_-]/g, "") === t.replace(/[\s_-]/g, ""));
};

const extractPotencyFromText = (p: ProductDisplay, type: "thc" | "cbd") => {
  // Enhanced potency extraction - preserve original units (mg vs %)
  const isEdible = p.category?.toLowerCase() === "edibles";
  
  // First check direct values (these are already properly formatted with units)
  const directValue = type === "thc" ? p.thc : p.cbd;
  if (directValue && directValue !== "0" && directValue !== "0%" && directValue !== "0mg") {
    // For edibles, if we get "100%" it's likely meant to be "100mg"
    if (isEdible && directValue.endsWith("%")) {
      const numMatch = directValue.match(/(\d+(?:\.\d+)?)/);
      if (numMatch) {
        return `${numMatch[1]}mg`;
      }
    }
    return directValue; // Return as-is to preserve units ("100mg" or "15%")
  }

  // Then check potency objects and format based on unit
  const potencyObj = type === "thc" ? (p as any).potencyThc : (p as any).potencyCbd;
  if (potencyObj && potencyObj.formatted) {
    const match = potencyObj.formatted.match(/(\d+(?:\.\d+)?)/)
    if (match && match[1]) {
      // Use the unit from the potency object, or infer based on category
      let unit = potencyObj.unit;
      if (!unit) {
        unit = potencyObj.formatted.includes('mg') ? 'mg' : (isEdible ? 'mg' : '%');
      }
      return `${match[1]}${unit}`;
    }
  }
  
  // Finally try to extract from text with % or mg
  const rxPercent = new RegExp(`${type}:?\\s*(\\d+(?:\\.\\d+)?)\\s*%`, "i");
  const rxMg = new RegExp(`${type}:?\\s*(\\d+(?:\\.\\d+)?)\\s*mg`, "i");
  const productText = `${p.name ?? ""} ${p.description ?? ""}`;
  
  // For edibles, prefer mg
  const mgMatch = productText.match(rxMg);
  if (mgMatch?.[1]) {
    return `${mgMatch[1]}mg`;
  }
  
  const percentMatch = productText.match(rxPercent);
  if (percentMatch?.[1]) {
    // For edibles, convert % to mg assumption
    return isEdible ? `${percentMatch[1]}mg` : `${percentMatch[1]}%`;
  }
  
  return null;
};

const extractWeightFromText = (p: ProductDisplay) => {
  const txt = `${p.name ?? ""} ${p.description ?? ""}`.toLowerCase();
  const w = txt.match(/\b(\.?(?:\d+)?(?:\.\d+)?)\s*(?:g|gram)s?\b/i);
  return w?.[1] ? `${w[1]}g` : null;
};

export default function ProductCard({
  product,
  buttonClassName,
  variant = "card",
}: ProductCardProps) {
  const { addToCart } = useCart();
  const { getBundleDeal } = useSpecials();

  /* ---------- Derived/Product helpers ---------- */
  const brandName = (product.brand as any)?.name ?? (product.brand as string) ?? "Unknown Brand";
  
  // Check if product is part of a bundle deal (dynamic from API)
  const bundleDeal = getBundleDeal(product.id);

  // Clean up product name (strip brand prefix + collapse pipe‐delimited metadata)
  let cleanName = product.name ?? "Untitled Product";
  if (cleanName.includes("|")) {
    const [strain, type] = cleanName.split("|").map((p) => p.trim());
    cleanName = `${strain} ${type?.replace(/\s*\|.*$/, "").trim()}`;
  }
  if (cleanName.toLowerCase().startsWith(brandName.toLowerCase())) {
    cleanName = cleanName.slice(brandName.length).replace(/^[:|\-]\s*/, "").trim() || cleanName;
  }

  // Potency helpers using enhanced extraction logic
  const thcDisplay = extractPotencyFromText(product, "thc");
  const cbdDisplay = extractPotencyFromText(product, "cbd");
  const showCBD = !!cbdDisplay && cbdDisplay !== "0%";

  // Sale / discount helpers
  const isOnSale = product.specialPrice !== undefined && product.specialPrice < (product.price ?? 0);
  const discountPct = isOnSale ? Math.round(((product.price ?? 0) - (product.specialPrice ?? 0)) / (product.price || 1) * 100) : 0;
  
  // Stock check - prevent adding out-of-stock items
  const isOutOfStock = product.inStock === false;
  const stockLeft = totalQuantity(product as any);
  const isLowStock = !isOutOfStock && stockLeft > 0 && stockLeft < 5;

  const isInfused = (product as any).isInfused || /infused|kief|hash|rosin|live resin|diamond|distillate|sauce|wax/i.test(`${product.name ?? ""} ${product.description ?? ""}`);
  const isPack = (product as any).isPack || /\b\d+\s*(?:p(?:ac)?k|ct)\b|multi-?pack/i.test(`${product.name ?? ""}`);

  const weight = (product as any).normalizedWeight ?? product.option ?? extractWeightFromText(product);

  // CTA colour fallback by category
  buttonClassName =
    buttonClassName || (product.category === "pre-rolls" ? "bg-amber-600 hover:bg-amber-700" : "bg-green-600 hover:bg-green-700");

  /* ---------- LIST VARIANT ---------- */
  if (variant === "list") {
    return (
      <Card className="group border border-gray-200 hover:border-green-300 hover:shadow-md transition rounded-lg bg-white">
        <Link
          href={`/shop/product/${createProductSlug(product)}?from=${encodeURIComponent(
            typeof window !== "undefined" ? window.location.pathname + window.location.search : "/shop"
          )}`}
          className="flex items-center p-4 gap-4 hover:bg-gray-50 transition-colors"
          onClick={() => {
            if (typeof window !== "undefined") {
              const ref = window.location.pathname + window.location.search;
              sessionStorage.setItem("productReferrer", ref);
            }
          }}
        >
          {/* image - larger for improved list view */}
          <div className="relative w-32 h-32 flex-shrink-0 bg-gray-50 rounded-lg overflow-hidden">
            <DirectImage src={product.image || "/images/placeholder-product.png"} alt={cleanName} fill className="object-cover" sizes="128px" />
            {/* Bundle Deal Badge for list view */}
            {bundleDeal && !isOnSale && (
              <div className="absolute top-2 right-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow">
                {bundleDeal.dealFormat || "Bundle"}
              </div>
            )}
          </div>

          {/* info */}
          <div className="flex-grow min-w-0 pr-4 py-2">
            <div className="text-[10px] font-bold uppercase text-green-700 tracking-wide mb-1">
              {brandName}
            </div>
            <h3 className="font-medium text-gray-900 text-base leading-tight line-clamp-2 mb-1 group-hover:text-green-700 transition">{cleanName}</h3>
            
            {weight && product.category?.toLowerCase() !== "accessories" && (
              <div className="mt-1 text-xs text-gray-600 flex items-center gap-2">
                <span className="font-medium">{weight}</span>
                {isPack && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 text-[10px] rounded-full">Pack</span>}
              </div>
            )}

            {product.strainType && isValidStrainType(product.strainType) && (
              <div className="mt-2">
                <span
                  className={`inline-block text-[11px] px-2 py-0.5 rounded-full ${
                    product.strainType.toLowerCase().includes("indica") && product.strainType.toLowerCase().includes("hybrid")
                      ? "bg-purple-100 text-purple-800"  // Indica Hybrid -> Purple
                      : product.strainType.toLowerCase().includes("sativa") && product.strainType.toLowerCase().includes("hybrid")
                      ? "bg-yellow-100 text-yellow-800"   // Sativa Hybrid -> Yellow
                      : product.strainType.toLowerCase().includes("indica") && !product.strainType.toLowerCase().includes("hybrid")
                      ? "bg-purple-100 text-purple-800"  // Pure Indica -> Purple
                      : product.strainType.toLowerCase().includes("sativa") && !product.strainType.toLowerCase().includes("hybrid")
                      ? "bg-yellow-100 text-yellow-800"   // Pure Sativa -> Yellow
                      : product.strainType.toLowerCase().includes("hybrid")
                      ? "bg-green-100 text-green-800"     // Pure Hybrid -> Green
                      : "bg-gray-100 text-gray-800"       // Unknown -> Gray
                  }`}
                >
                  {normalizeStrainType(product.strainType)}
                </span>
              </div>
            )}

            {product.category?.toLowerCase() === "accessories" ? (
              product.subcategory && product.subcategory.toLowerCase() !== "default" && (
                <div className="mt-2 text-xs text-gray-700">
                  <span className="bg-gray-100 px-2 py-0.5 rounded-full">
                    {product.subcategory.replace(/_/g, " ")}
                  </span>
                </div>
              )
            ) : (
              (thcDisplay || cbdDisplay) && (
                <div className="mt-2 flex gap-2 flex-wrap text-xs text-gray-700">
                  {thcDisplay && <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">THC: {thcDisplay}</span>}
                  {showCBD && <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">CBD: {cbdDisplay}</span>}
                </div>
              )
            )}
          </div>

          {/* price & add */}
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            {isOnSale ? (
              <div>
                <div className="text-red-600 font-semibold text-lg">{formatPrice(product.specialPrice)}</div>
                <div className="text-gray-400 text-sm line-through">{formatPrice(product.price)}</div>
              </div>
            ) : (
              <span className="font-semibold text-lg">{formatPrice(product.price)}</span>
            )}
            <Button
              size="sm"
              className={`${buttonClassName} w-9 h-9 p-0 rounded-full shadow-md hover:scale-110 transition-transform ${isOutOfStock ? 'opacity-50 cursor-not-allowed' : ''}`}
              aria-label={isOutOfStock ? "Out of stock" : "Add to cart"}
              title={isOutOfStock ? "Out of stock" : "Add to cart"}
              disabled={isOutOfStock}
              onClick={(e) => {
                e.preventDefault();
                if (isOutOfStock) return;
                addToCart(product, 1);

                if (typeof window !== "undefined") {
                  const n = document.createElement("div");
                  n.className =
                    "fixed bottom-6 right-6 bg-green-600 text-white px-4 py-3 rounded-xl shadow-xl flex items-center opacity-0 translate-y-2 transition z-50";
                  n.innerHTML = `<svg xmlns='http://www.w3.org/2000/svg' class='h-5 w-5 mr-2' fill='currentColor' viewBox='0 0 20 20'><path fill-rule='evenodd' d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z' clip-rule='evenodd'/></svg><span>${cleanName} added to cart</span>`;
                  document.body.appendChild(n);
                  setTimeout(() => n.classList.remove("opacity-0", "translate-y-2"), 50);
                  setTimeout(() => {
                    n.classList.add("opacity-0", "translate-y-2");
                    setTimeout(() => document.body.removeChild(n), 500);
                  }, 2500);
                }
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 5v14" />
                <path d="M5 12h14" />
              </svg>
            </Button>
          </div>
        </Link>
      </Card>
    );
  }

  /* ---------- CARD VARIANT ---------- */
  return (
    <Card className="group border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-200 rounded-xl h-full flex flex-col bg-white overflow-hidden">
      <Link
        href={`/shop/product/${createProductSlug(product)}?from=${encodeURIComponent(
          typeof window !== "undefined" ? window.location.pathname + window.location.search : "/shop"
        )}`}
        className="flex-grow flex flex-col"
        onClick={() => {
          if (typeof window !== "undefined") {
            const ref = window.location.pathname + window.location.search;
            sessionStorage.setItem("productReferrer", ref);
          }
        }}
      >
        {/* image block - square on mobile, slightly taller on desktop */}
        <div className="relative bg-gradient-to-b from-gray-50 to-white aspect-square sm:aspect-[5/6] overflow-hidden flex items-center justify-center">
          <DirectImage
            src={product.image || "/images/placeholder-product.png"}
            alt={cleanName}
            width={160}
            height={160}
            className="object-contain group-hover:scale-105 transition-transform duration-300 p-1"
            style={{ objectPosition: "center" }}
          />

          {isOnSale && discountPct > 0 && (
            <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 bg-gradient-to-r from-red-500 to-red-600 text-white text-[10px] sm:text-xs font-bold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full shadow-sm">
              {discountPct}% OFF
            </div>
          )}

          {product.staffPick && (
            <div className="absolute top-1.5 left-1.5 sm:top-2 sm:left-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-[10px] sm:text-xs font-bold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full shadow-sm">
              Staff Pick
            </div>
          )}

          {isInfused && product.category === "pre-rolls" && (
            <div className="absolute bottom-1.5 left-1.5 sm:bottom-2 sm:left-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white text-[10px] sm:text-xs font-bold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full shadow-sm">
              Infused
            </div>
          )}
          
          {/* Bundle Deal Badge - Dynamic from Dutchie API */}
          {bundleDeal && !isOnSale && (
            <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-[9px] sm:text-[10px] font-bold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full shadow-sm">
              {bundleDeal.dealFormat || "Bundle Deal"}
            </div>
          )}

          {/* Low stock urgency chip. Bottom-right so it doesn't collide with
              the sale/staff-pick/bundle badges up top. */}
          {isLowStock && (
            <div className="absolute bottom-1.5 right-1.5 sm:bottom-2 sm:right-2">
              <LowStockBadge quantity={stockLeft} compact />
            </div>
          )}
        </div>

        {/* content - minimal on mobile */}
        <CardContent className="p-1.5 sm:p-2.5 flex-grow border-t border-gray-50">
          <div className="text-[8px] sm:text-[10px] font-bold uppercase text-green-700 tracking-wide">
            {brandName}
          </div>
          <h3 className="text-[11px] sm:text-xs font-medium text-gray-900 group-hover:text-green-700 transition line-clamp-2 leading-tight mt-0.5">
            {cleanName}
          </h3>

          {/* Compact info row - weight + strain on same line */}
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {weight && product.category?.toLowerCase() !== "accessories" && (
              <span className="text-[9px] sm:text-[10px] text-gray-500 font-medium">{weight}</span>
            )}
            {product.strainType && isValidStrainType(product.strainType) && (
              <span
                className={`inline-block text-[8px] sm:text-[10px] px-1 sm:px-1.5 py-0.5 rounded-full ${
                  product.strainType.toLowerCase().includes("indica")
                    ? "bg-purple-100 text-purple-700"
                    : product.strainType.toLowerCase().includes("sativa")
                    ? "bg-yellow-100 text-yellow-700"
                    : product.strainType.toLowerCase().includes("hybrid")
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {normalizeStrainType(product.strainType)}
              </span>
            )}
          </div>
          
          {/* THC/CBD potency display - show for non-accessory products */}
          {product.category?.toLowerCase() !== "accessories" && (thcDisplay || showCBD) && (
            <div className="mt-1 flex flex-wrap items-center gap-1">
              {thcDisplay && (
                <span className="text-[8px] sm:text-[10px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded-full font-medium">
                  THC {thcDisplay}
                </span>
              )}
              {showCBD && (
                <span className="text-[8px] sm:text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">
                  CBD {cbdDisplay}
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Link>

      {/* footer CTA - compact */}
      <CardFooter className="p-1.5 sm:p-2.5 flex items-center justify-between border-t border-gray-50 bg-white mt-auto">
        {isOnSale ? (
          <div>
            <div className="text-red-600 font-bold text-xs sm:text-sm">{formatPrice(product.specialPrice)}</div>
            <div className="text-gray-400 text-[9px] sm:text-[10px] line-through">{formatPrice(product.price)}</div>
          </div>
        ) : (
          <span className="font-bold text-xs sm:text-sm text-gray-900">{formatPrice(product.price)}</span>
        )}

        <Button
          size="sm"
          className={`${buttonClassName} w-6 h-6 sm:w-7 sm:h-7 p-0 rounded-full shadow-md hover:scale-110 transition-all duration-200 ${isOutOfStock ? 'opacity-50 cursor-not-allowed' : ''}`}
          aria-label={isOutOfStock ? "Out of stock" : "Add to cart"}
          title={isOutOfStock ? "Out of stock" : "Add to cart"}
          disabled={isOutOfStock}
          onClick={(e) => {
            e.preventDefault();
            if (isOutOfStock) return;
            addToCart(product, 1);

            if (typeof window !== "undefined") {
              const n = document.createElement("div");
              n.className =
                "fixed bottom-6 right-6 bg-green-600 text-white px-4 py-3 rounded-xl shadow-xl flex items-center opacity-0 translate-y-2 transition z-50";
              n.innerHTML = `<svg xmlns='http://www.w3.org/2000/svg' class='h-5 w-5 mr-2' fill='currentColor' viewBox='0 0 20 20'><path fill-rule='evenodd' d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z' clip-rule='evenodd'/></svg><span>${cleanName} added to cart</span>`;
              document.body.appendChild(n);
              setTimeout(() => n.classList.remove("opacity-0", "translate-y-2"), 50);
              setTimeout(() => {
                n.classList.add("opacity-0", "translate-y-2");
                setTimeout(() => document.body.removeChild(n), 500);
              }, 2500);
            }
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="sm:w-4 sm:h-4"
          >
            <path d="M12 5v14" />
            <path d="M5 12h14" />
          </svg>
        </Button>
      </CardFooter>
    </Card>
  );
}
